import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import { nanoid } from 'nanoid';
import type { TribeId } from '@/content/xcloneHandles';
import { usePower } from './power';
import { useSettings } from './settings';
import { pickYReplyTree, pickYReplyResponse } from '@/content/dialogue/yReplies';
import type { YReplyChoice } from '@/content/dialogue/yReplies';

export interface XPost {
  id: string;
  authorHandle: string;
  authorTribe?: TribeId;
  authorIsSelf?: boolean;
  text: string;
  timestamp: number;
  likes: number;
  reposts: number;
  read: boolean;
  likedBySelf: boolean;
  repostedBySelf: boolean;
}

interface XCloneStore {
  posts: XPost[]; // newest first
  followers: number;
  composePost: (text: string) => void;
  receivePost: (p: { authorHandle: string; authorTribe?: TribeId; text: string; likes?: number; reposts?: number }) => void;
  toggleLike: (id: string) => void;
  toggleRepost: (id: string) => void;
  reply: (toId: string, text: string) => void;
  // Quick-reply: takes a YReplyChoice. Auto-picks an in-tribe NPC response.
  replyChoice: (toId: string, choice: YReplyChoice) => void;
  markAllRead: () => void;
  bumpFollowers: (delta: number) => void;
}

export const useXClone = create<XCloneStore>()(
  immer((set) => ({
    posts: [],
    followers: 14,
    composePost: (text) => set((s) => {
      const trimmed = text.trim();
      if (!trimmed) return;
      s.posts.unshift({
        id: nanoid(),
        authorHandle: 'you',
        authorIsSelf: true,
        text: trimmed,
        timestamp: Date.now(),
        likes: 0,
        reposts: 0,
        read: true,
        likedBySelf: false,
        repostedBySelf: false,
      });
      capPosts(s);

      // Compute tribal signal & follower delta. Side-effects via getState() because
      // they're cross-store; intentionally outside the immer set to avoid coupling.
      // (Run on next microtask so this set() finishes first.)
      queueMicrotask(() => applyPostSideEffects(trimmed));
    }),
    receivePost: ({ authorHandle, authorTribe, text, likes = 0, reposts = 0 }) => set((s) => {
      s.posts.unshift({
        id: nanoid(),
        authorHandle,
        authorTribe,
        text,
        timestamp: Date.now(),
        likes,
        reposts,
        read: false,
        likedBySelf: false,
        repostedBySelf: false,
      });
      capPosts(s);
    }),
    toggleLike: (id) => set((s) => {
      const p = s.posts.find((x) => x.id === id);
      if (!p) return;
      if (p.likedBySelf) { p.likes--; p.likedBySelf = false; }
      else { p.likes++; p.likedBySelf = true; }
    }),
    toggleRepost: (id) => set((s) => {
      const p = s.posts.find((x) => x.id === id);
      if (!p) return;
      if (p.repostedBySelf) { p.reposts--; p.repostedBySelf = false; }
      else { p.reposts++; p.repostedBySelf = true; }
    }),
    reply: (toId, text) => {
      const trimmed = text.trim();
      if (!trimmed) return;
      const target = useXClone.getState().posts.find((p) => p.id === toId);
      if (!target) return;
      // Reply is a top-level post that quotes the original handle as @-mention. Same
      // tribal/follower side-effects as a regular composePost so replies are real signal.
      const selfHandle = useSettings.getState().playerHandle || 'you';
      const replyText = `@${target.authorIsSelf ? selfHandle : target.authorHandle} ${trimmed}`;
      set((s) => {
        s.posts.unshift({
          id: nanoid(),
          authorHandle: 'you',
          authorIsSelf: true,
          text: replyText,
          timestamp: Date.now(),
          likes: 0,
          reposts: 0,
          read: true,
          likedBySelf: false,
          repostedBySelf: false,
        });
        capPosts(s);
        // Original post gets a tiny attention boost
        const orig = s.posts.find((p) => p.id === toId);
        if (orig) {
          orig.likes += 1;
          orig.reposts += Math.random() < 0.3 ? 1 : 0;
        }
      });
      queueMicrotask(() => applyPostSideEffects(replyText));

      // When you reply directly to a non-self post, the ORIGINAL NPC (not just
      // their tribe) replies back personally ~70% of the time. Was missing —
      // tribe-reactions felt unrelated to your actual reply target.
      if (!target.authorIsSelf && Math.random() < 0.7) {
        const lc = trimmed.toLowerCase();
        const positive = /\b(thanks|love|please|sorry|sure|ok|will do|absolutely|yes|of course|appreciate|king|based|legend|respect)\b/.test(lc);
        const negative = /\b(no|fuck|stop|leave|never|hate|cope|seethe|fraud|scam|grift|cringe|delete)\b/.test(lc);
        const tribe = target.authorTribe;
        const personalReply = pickPersonalReply(tribe, positive, negative);
        if (personalReply) {
          const delay = 4_000 + Math.floor(Math.random() * 12_000);
          setTimeout(() => {
            useXClone.getState().receivePost({
              authorHandle: target.authorHandle,
              authorTribe: tribe,
              text: `@${selfHandle} ${personalReply}`,
              likes: 5 + Math.floor(Math.random() * 200),
              reposts: Math.floor(Math.random() * 30),
            });
          }, delay);
        }
      }
    },
    replyChoice: (toId, choice) => {
      const target = useXClone.getState().posts.find((p) => p.id === toId);
      if (!target) return;
      const selfHandle = useSettings.getState().playerHandle || 'you';
      const replyText = `@${target.authorIsSelf ? selfHandle : target.authorHandle} ${choice.playerSays}`;
      // Add the player's reply post
      set((s) => {
        s.posts.unshift({
          id: nanoid(),
          authorHandle: 'you',
          authorIsSelf: true,
          text: replyText,
          timestamp: Date.now(),
          likes: 0,
          reposts: 0,
          read: true,
          likedBySelf: false,
          repostedBySelf: false,
        });
        capPosts(s);
        const orig = s.posts.find((p) => p.id === toId);
        if (orig) {
          orig.likes += 1;
          orig.reposts += Math.random() < 0.3 ? 1 : 0;
        }
      });
      // Standard side effects (tribal rep / followers)
      queueMicrotask(() => applyPostSideEffects(replyText));
      // The original NPC replies back personally based on the chosen valence.
      if (!target.authorIsSelf) {
        const tree = pickYReplyTree({ text: target.text, tribe: target.authorTribe });
        const valence = choice.valence ?? 'agree';
        const npcReply = pickYReplyResponse(tree, target.authorTribe, valence);
        if (npcReply) {
          const delay = 4_000 + Math.floor(Math.random() * 12_000);
          setTimeout(() => {
            useXClone.getState().receivePost({
              authorHandle: target.authorHandle,
              authorTribe: target.authorTribe,
              text: `@${selfHandle} ${npcReply}`,
              likes: 5 + Math.floor(Math.random() * 200),
              reposts: Math.floor(Math.random() * 30),
            });
          }, delay);
        }
      }
    },
    markAllRead: () => set((s) => {
      for (const p of s.posts) p.read = true;
    }),
    bumpFollowers: (delta) => set((s) => {
      s.followers = Math.max(0, s.followers + delta);
    }),
  })),
);

function capPosts(s: { posts: XPost[] }): void {
  if (s.posts.length > 200) s.posts.length = 200;
}

/**
 * Per POWER.md §X-Clone player posting: parse content for tribal signals
 * (keywords + ticker mentions + hashtags), apply per-tribe rep deltas,
 * compute viral follower delta, and schedule a few NPC replies/quotes
 * over the next ~30 seconds so posting feels reactive.
 */
function applyPostSideEffects(text: string): void {
  const t = text.toLowerCase();
  const power = usePower.getState();

  // Crude keyword → tribe alignment table. Phase 4 makes this content-driven.
  const SIGNALS: Array<{ pattern: RegExp; tribe: TribeId; delta: number }> = [
    { pattern: /\bguh\b|\byolo\b|💎|0dte/i, tribe: 'wsb', delta: 3 },
    { pattern: /\bregards?\b|\balpha\b|\bcope\b|\bdunk\b/i, tribe: 'fintwit', delta: 2 },
    { pattern: /\btariff\b|maga|🇺🇸|🦅/i, tribe: 'maga', delta: 3 },
    { pattern: /\bbiden\b|\besg\b|\bbillionair/i, tribe: 'left', delta: 2 },
    { pattern: /\bcrypto\b|wagmi|ngmi|🚀|moon|rug/i, tribe: 'crypto', delta: 3 },
    { pattern: /\bjpow\b|\bfomc\b|\bcpi\b/i, tribe: 'cnbc', delta: 1 },
    { pattern: /\bbreaking\b|leaked|sources/i, tribe: 'press', delta: 1 },
    { pattern: /\bmom\b/i, tribe: 'mom', delta: 1 },
  ];

  // Conflict pairs — pleasing one usually offends another
  const CONFLICTS: Array<[TribeId, TribeId]> = [
    ['maga', 'left'],
    ['crypto', 'cnbc'],
    ['wsb', 'fintwit'],
  ];

  let viralBoost = 1;
  for (const { pattern, tribe, delta } of SIGNALS) {
    if (pattern.test(t)) {
      power.bumpRep(tribe, delta);
      viralBoost += 0.3;
      // ripple negative to conflict pair
      for (const [a, b] of CONFLICTS) {
        if (a === tribe) power.bumpRep(b, -Math.ceil(delta / 2));
        if (b === tribe) power.bumpRep(a, -Math.ceil(delta / 2));
      }
    }
  }

  // Notoriety small bump
  power.bumpNotoriety(0.5);

  // Follower delta: deterministic-ish from text length × viralBoost
  const followerDelta = Math.floor((Math.min(text.length, 280) / 30) * viralBoost);
  if (followerDelta !== 0) {
    useXCloneInternal.getState().bumpFollowers(followerDelta);
    // Surface meaningful follower jumps so the player sees their reach grow.
    if (followerDelta >= 10) {
      import('@/state/notifications').then((m) => {
        m.useNotifications.getState().push({
          surfaceId: 'x-clone',
          severity: 'info',
          text: `+${followerDelta} followers from that post`,
        });
      });
    }
  }

  // Schedule 1-3 NPC reactions to fire over the next ~30 seconds
  scheduleNpcReactions(t);
}

const POSITIVE_REACTIONS: Record<string, string[]> = {
  wsb: [
    'KING',
    'this guy gets it. respect.',
    'hard agree. yolo intensifies.',
    'i am following this person blindly. i refuse to read the post.',
  ],
  fintwit: [
    'controversial take. mostly because it\'s wrong. but i respect the conviction.',
    'this is the kind of post that makes me want to short more.',
    'this you?',
  ],
  maga: [
    'BASED. cope, libs.',
    'this is what real America thinks. media won\'t cover it.',
    'finally someone with a spine.',
  ],
  left: [
    'finally a voice of reason. one reasonable point in your entire life. we will be screencapping.',
    'thank you for saying what we were all afraid to say.',
  ],
  crypto: [
    'WAGMI fr 🚀🚀',
    'real recognize real. ngmi if you don\'t see this.',
    'i would buy his bag based on this energy alone.',
  ],
  press: [
    'we have reached out for comment.',
    'screenshotting this for a future story we will not tag you in.',
  ],
  cnbc: [
    'we should have him on the show.',
  ],
};

const NEGATIVE_REACTIONS: Record<string, string[]> = {
  wsb: [
    'ratio',
    'this is the worst take i have read this hour. and it is 11pm on a tuesday.',
    'GUH energy. blocked.',
  ],
  fintwit: [
    'never been so confidently wrong in 280 chars. impressive.',
    'this thread is going to age like milk. unrefrigerated milk. on a beach. in arizona.',
    'imagine having this take and also being wrong about chart patterns. devastating.',
  ],
  maga: [
    'paid by soros. reported.',
    'this is exactly why we lost. you are why we lost.',
    'rino energy. unfollowed and reported.',
  ],
  left: [
    'this is exactly the kind of unhinged take that lets fascism win. tweeted as i scroll past 14 ads on this same app.',
    'imagine being this confidently wrong and also a paying meta verified subscriber.',
  ],
  crypto: [
    'NGMI. ratioed by a 14yo with a stock photo PFP.',
    'this guy is the exit liquidity AND a moralist. devastating combo.',
  ],
  press: [
    'we will be writing about this. unfortunately.',
  ],
  cnbc: [
    'if this is your investment thesis, please call your mother.',
  ],
};

const TRIBE_HANDLES: Record<string, string[]> = {
  wsb: ['YOLOPapi', 'GUHGod69', 'GapAndCrap', 'BrokeBoiPaul', 'wsbBagholder', 'wsbDoomer', 'David_Defiant'],
  fintwit: ['MacroDad42', 'TaylorBeartrap', 'AnastasiaPriceman', 'MarcusRothfeld_VC', 'ChartWizardLLC'],
  cnbc: ['JimCranier', 'TiaBanzai', 'CNBCSquawk', 'CnbcWatcher'],
  maga: ['PatriotPete1776', 'FreedomEagleX', 'RonaldGrumpsayer'],
  left: ['ProfPolicyWonk', 'SocialistPapaya'],
  crypto: ['WAGMI_VIP', 'ShitcoinPaul', 'DegenWolfie', '0xVitaliKHimself', 'CryptoCucked'],
  press: ['ReutersFlash', 'WSJ_RealTimeBait', 'JaneTheJournalist', 'PressFlash'],
  mom: ['MomDigital', 'WifeNetwork'],
};

// Personal NPC-reply pools by tribe — used when the player replies directly to
// an NPC's post and the NPC themselves comes back at the player. Distinct from
// the tribe-broadcast POSITIVE/NEGATIVE_REACTIONS.
const PERSONAL_POSITIVE: Record<TribeId, string[]> = {
  wsb: ['🙏 finally someone gets it', 'love u brother. WAGMI.', 'this is exactly the energy. degenerate forever.', 'u get it. follow back.'],
  fintwit: ['fair point. for once.', 'genuinely respect this take. mostly.', 'agreed. on the record. probably.', 'fine. one for you.'],
  cnbc: ['Appreciate the engagement. Would you come on the show.', 'Thanks for watching. Send a Linkedin.', 'Noted. We may quote you. Without permission.'],
  maga: ['BASED RESPONSE PATRIOT 🇺🇸🦅', 'see this is what real americans look like.', 'follow me brother. follow.', 'this is the level of energy we need.'],
  left: ['rare W.', 'okay this one is actually good.', 'i don\'t hate this. don\'t make a thing of it.', 'fine. fair.'],
  crypto: ['gm 🚀', 'WAGMI my G. dm me a wallet, sending you a coin.', 'based take. follow back. u r early.', 'this is alpha. quietly.'],
  press: ['Thank you for your reply. Going on the record?', 'Noted. Will reach out for full quote.', 'Appreciate the response. Source confirmed.'],
  mom: ['oh that\'s nice sweetie. love mom.', 'love you ❤️', 'proud of you for engaging online! love mom.'],
};
const PERSONAL_NEGATIVE: Record<TribeId, string[]> = {
  wsb: ['cope and seethe nerd', 'L take. paper hands forever.', 'shut up and post your portfolio', 'nah u r ngmi'],
  fintwit: ['this you?', 'genuinely a top-decile bad take.', 'okay logging off — this is too embarrassing to engage with.', 'imagine typing this with confidence.'],
  cnbc: ['Noted. We will not be reaching out.', 'We do not engage with tone like this.', 'Best of luck.'],
  maga: ['LOSER ENERGY. SAD.', 'cry harder lib.', 'this is why america is failing. you.', 'you are the problem with this country.'],
  left: ['exactly the take we expected from this account.', 'screenshotting this for the group chat.', 'okay champ.', 'block button is right there for a reason.'],
  crypto: ['ngmi 😂', 'cope harder. price still going up.', 'have fun staying poor.', 'you fade because you\'re scared. that\'s ok.'],
  press: ['Noted. Off the record.', 'Hostile reply logged. We may publish.', 'Counsel will be in touch.'],
  mom: ['don\'t talk to your mother that way. ON THE INTERNET.', 'sweetie. okay. i love you anyway.'],
};
const PERSONAL_NEUTRAL: Record<TribeId, string[]> = {
  wsb: ['k', 'lol', 'idk man', 'maybe', 'sure'],
  fintwit: ['noted.', 'fair.', 'received.', 'mm.'],
  cnbc: ['Noted.', 'Received.', 'Logged.'],
  maga: ['ok', 'noted patriot', 'sure'],
  left: ['k', 'noted', 'sure'],
  crypto: ['gm', 'wen', 'lfg', 'ok ser'],
  press: ['Noted.', 'On background?', 'Off the record?'],
  mom: ['ok sweetie', 'k love mom', 'mm hmm'],
};

function pickPersonalReply(tribe: TribeId | undefined, positive: boolean, negative: boolean): string | null {
  if (!tribe) {
    // No tribe info — generic anon
    if (positive) return 'thanks for the reply';
    if (negative) return 'noted.';
    return 'k';
  }
  const pool = positive ? PERSONAL_POSITIVE[tribe]
             : negative ? PERSONAL_NEGATIVE[tribe]
             : PERSONAL_NEUTRAL[tribe];
  if (!pool || pool.length === 0) return null;
  return pool[Math.floor(Math.random() * pool.length)] ?? null;
}

function scheduleNpcReactions(textLower: string): void {
  // Pick 1-3 tribes to react. Bias toward tribes signaled by text.
  const reactingTribes: TribeId[] = [];
  for (const [pattern, tribe] of [
    [/\bguh\b|\byolo\b|💎|0dte/i, 'wsb'],
    [/\btariff\b|maga|🇺🇸|🦅/i, 'maga'],
    [/\bcrypto\b|wagmi|🚀|moon/i, 'crypto'],
    [/\besg\b|biden|billionair/i, 'left'],
  ] as const) {
    if (pattern.test(textLower)) {
      reactingTribes.push(tribe as TribeId);
      // Antagonist tribes also react, but negative
      if (tribe === 'maga') reactingTribes.push('left' as TribeId);
      if (tribe === 'left') reactingTribes.push('maga' as TribeId);
      if (tribe === 'crypto') reactingTribes.push('cnbc' as TribeId);
    }
  }
  // If no signal, pick a random "press" or "wsb" reaction
  if (reactingTribes.length === 0) {
    reactingTribes.push((Math.random() > 0.5 ? 'press' : 'wsb') as TribeId);
  }

  // Take up to 3
  const tribesToFire = reactingTribes.slice(0, 3);
  for (const tribe of tribesToFire) {
    // Polarity: was this tribe being addressed positively in the text?
    const positive = ['wsb', 'maga', 'crypto', 'left'].includes(tribe) && reactingTribes[0] === tribe;
    const pool = positive ? POSITIVE_REACTIONS[tribe] : NEGATIVE_REACTIONS[tribe];
    if (!pool || pool.length === 0) continue;
    const text = pool[Math.floor(Math.random() * pool.length)]!;
    const handles = TRIBE_HANDLES[tribe];
    const handle = handles?.[Math.floor(Math.random() * (handles?.length ?? 1))] ?? 'anon';
    const delayMs = 3_000 + Math.floor(Math.random() * 25_000);
    setTimeout(() => {
      useXClone.getState().receivePost({
        authorHandle: handle,
        authorTribe: tribe,
        text,
        likes: 30 + Math.floor(Math.random() * 4000),
        reposts: 2 + Math.floor(Math.random() * 700),
      });
    }, delayMs);
  }
}

// Internal helper store-handle to avoid circular import at module load.
const useXCloneInternal = {
  getState: () => useXClone.getState() as XCloneStore & { bumpFollowers: (n: number) => void },
};
